import {
  createContext, useContext, useState, useMemo, PropsWithChildren, ReactNode,
} from 'react';
import styled, { keyframes, css } from 'styled-components';
import { v4 as uuid } from 'uuid';
import { InformationIcon, SuccessIcon, WarningIcon } from '../../../..';
import { mobileCss, useStyledTheme } from '../../../styling';
import { Flex } from '../../Flex';

// eslint-disable-next-line no-shadow
export enum ToastStatus {
    SUCCESS,
    INFO,
    ERROR,
    WARNING,
    PURPLE,
}

export type ToastNotificationType = {
  text: string | ReactNode,
  type: ToastStatus,
  showCloseIcon?: boolean,
  isExiting?: boolean,
}

type ToastNotificationsContextType = {
  toastNotifications: Record<string, ToastNotificationType>,
  addToastNotification: (toastNotification: ToastNotificationType) => void,
  removeToastNotification: (id: string) => void,
}

const defaultToastNotificationsContext = {
  toastNotifications: {},
  addToastNotification: () => {},
  removeToastNotification: () => {},
};

const ToastNotificationsContext = createContext<ToastNotificationsContextType>(defaultToastNotificationsContext);

const Container = styled.div`${({ theme }) => `
  z-index:999;
  position: fixed;
  bottom:0;
  left:0;
  width:40%;
  pointer-events: none;
  bottom: ${theme.padding[5]};
  left: ${theme.padding[5]};
  right: ${theme.padding[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.padding[1]};
  ${mobileCss(`
    width:100%;
  `)}
`}`;

const slideInUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideOutDown = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
`;

export const ToastNotification = styled.div<{color?: string, isExiting?: boolean}>`${({ theme, color, isExiting }) => css`
  background: ${color};
  border-radius:${theme.borderRadius[2]};
  pointer-events: auto;
  box-shadow: 0px 2px 4px -2px rgba(16, 24, 40, 0.06), 0px 4px 8px -2px rgba(16, 24, 40, 0.10);
  animation: ${isExiting ? slideOutDown : slideInUp} 0.3s ease-in-out forwards;
`}`;

const ToastNotificationInner = styled(Flex)`${({ theme }) => `
  align-items:center;
  gap: ${theme.padding[4]};
`}`;

const ToastNotificationIcon = styled(Flex) <{ color?: string }>`${({ theme, color }) => `
  padding: 0 ${theme.padding[2]};
  align-items: center;
  border-top-left-radius: ${theme.borderRadius[2]};
  border-bottom-left-radius: ${theme.borderRadius[2]};
  background-color: ${color};
  position: absolute;
  height: 100%;
`}`;

const ToastNotificationBody = styled(Flex) <{ color?: string}>`${({ theme, color }) => `
  width: 100%; 
  padding: calc(${theme.padding[2]} + ${theme.borderWidth[1]}) ${theme.padding[4]} calc(${theme.padding[2]} + ${theme.borderWidth[1]}) calc(${theme.padding[5]} + ${theme.padding[9]});
  background-color: ${color};
  font-size: ${theme.fontSize[2]};
  border-radius: ${theme.borderRadius[2]};
  align-items: center;
  color: ${theme.textPrimary};
  line-height: ${theme.fontSize[6]};
  min-height:  ${theme.alertMinHeight};
  box-sizing: border-box;
  justify-content: space-between;
`}`;

const ToastNotificationIconWrapper = styled.div`
  width: ${({ theme }) => theme.fontSize[7]};
  height: ${({ theme }) => theme.fontSize[7]};
`;

const ToastCloseIcon = styled.i<{showCloseIcon?: boolean}>`${({ theme, showCloseIcon }) => `
  color:${theme.textNeutralDark};
  font-size: ${theme.fontSize[3]};
  color: ${theme.colours.neutral[2]};
  cursor: pointer;
  padding-left: ${showCloseIcon ? theme.padding[9] : `calc( ${theme.fontSize[3]} + ${theme.padding[9]})`};
`}`;

function ToastNotifications({ children } : PropsWithChildren) {
  const { toastNotifications, removeToastNotification } = useContext<ToastNotificationsContextType>(ToastNotificationsContext);

  const theme = useStyledTheme();

  const ToastStatusToColor = {
    [ToastStatus.SUCCESS]: {
      color: theme.colours.success[1],
      background: theme.colours.success[2],
    },
    [ToastStatus.INFO]: {
      color: theme.colours.info[1],
      background: theme.colours.info[2],
    },
    [ToastStatus.ERROR]: {
      color: theme.colours.danger[1],
      background: theme.colours.danger[2],
    },
    [ToastStatus.WARNING]: {
      color: theme.colours.warning[1],
      background: theme.colours.warning[2],
    },
    [ToastStatus.PURPLE]: {
      color: theme.colours.warning[3],
      background: theme.colours.warning[4],
    },
  };

  const renderToast = (typeToast: ToastStatus) => {
    switch (typeToast) {
      case ToastStatus.SUCCESS:
        return <SuccessIcon fill={theme.colours.success[1]} />;
      case ToastStatus.INFO:
        return <InformationIcon fill={theme.colours.info[1]} />;
      case ToastStatus.WARNING:
        return <WarningIcon fill={theme.colours.warning[1]} />;
      case ToastStatus.ERROR:
        return <WarningIcon fill={theme.colours.danger[1]} />;
      case ToastStatus.PURPLE:
        return <WarningIcon fill={theme.colours.warning[3]} />;
      default:
        return <></>;
    }
  };

  return (
    <>
      <Container>
        {Object.entries(toastNotifications).map(([id, toastNotification]) => {
          const {
            text, type, showCloseIcon, isExiting,
          } = toastNotification;
          return (
            <ToastNotification color={ToastStatusToColor[type]?.background} key={id} isExiting={isExiting}>
              <ToastNotificationInner id="innerToast">
                <ToastNotificationIcon color={ToastStatusToColor[type]?.color}>
                  <ToastNotificationIconWrapper>
                    {renderToast(type)}
                  </ToastNotificationIconWrapper>
                </ToastNotificationIcon>
                <ToastNotificationBody>
                  {text}
                  <ToastCloseIcon showCloseIcon={showCloseIcon} className={showCloseIcon ? 'mi-close' : ''} onClick={() => removeToastNotification(id)} aria-hidden="true" />
                </ToastNotificationBody>
              </ToastNotificationInner>
            </ToastNotification>
          );
        })}
      </Container>
      {children}
    </>
  );
}

export function ToastNotificationProvider({ children, autoCloseTimeout = 5000 }: PropsWithChildren<{autoCloseTimeout?: number}>) {
  const [toastNotifications, setToastNotifications] = useState<Record<string, ToastNotificationType>>({});

  const removeToastNotification = (id: string) => {
    setToastNotifications((prev) => ({
      ...prev,
      [id]: { ...prev[id], isExiting: true },
    }));

    setTimeout(() => {
      setToastNotifications((prevNotifications) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [id]: remove, ...rest } = prevNotifications;
        return rest;
      });
    }, 300);
  };
  const addToastNotification = (toastNotification: ToastNotificationType) => setToastNotifications((prev) => {
    const id = uuid();

    setTimeout(() => {
      removeToastNotification(id);
    }, autoCloseTimeout);

    return {
      ...prev,
      [id]: toastNotification,
    };
  });

  const toastNotificationsValue = useMemo(() => ({
    toastNotifications,
    addToastNotification,
    removeToastNotification,
  }), [toastNotifications]);

  return (
    <ToastNotificationsContext.Provider value={toastNotificationsValue}>
      <ToastNotifications>
        {children}
      </ToastNotifications>
    </ToastNotificationsContext.Provider>
  );
}

export function useToastNotifications(): ToastNotificationsContextType {
  return useContext<ToastNotificationsContextType>(ToastNotificationsContext);
}
